/**
 * Trusscore Length Table - Panel Optimization Lookup
 *
 * Pre-computed optimal panel combinations for horizontal installations.
 * For a given target length (1-200 feet), provides the optimal mix of
 * 8', 10', 12', 14', 16', and 20' panels to minimize waste.
 *
 * Extended from original 100ft to 200ft for commercial projects.
 *
 * IMPORTANT: This table optimizes for SINGLE ROW installations.
 * The values are multiplied by the number of rows (SurfaceWidthinPanels)
 * in the calculation logic.
 *
 * FUTURE OPTIMIZATION OPPORTUNITY:
 * For sub-8' sections with multiple rows, longer panels could be cut into
 * multiple pieces to reduce waste. For example:
 *   - 4' section × 3 rows = 3 × 8' panels (current, 12' waste)
 *   - 4' section × 3 rows = 1 × 12' panel (optimized, 0' waste)
 * This would require changes to the calculation logic in panels.ts to
 * account for multi-row cutting optimization rather than per-row selection.
 */

import { LengthTableEntry } from '../types';

/**
 * Calculate optimal panel combination for a given length.
 * Uses the same empirical logic as the original table.
 */
function calculateOptimalPanels(targetLength: number): LengthTableEntry {
  const panelLengths = [20, 16, 14, 12, 10, 8]; // Sorted descending for greedy approach

  let remaining = targetLength;
  const counts: Record<string, number> = {
    '8': 0,
    '10': 0,
    '12': 0,
    '14': 0,
    '16': 0,
    '20': 0,
  };

  // Greedy algorithm: use largest panels first, minimize waste
  for (const panelLength of panelLengths) {
    while (remaining >= panelLength) {
      counts[String(panelLength)]++;
      remaining -= panelLength;
    }
  }

  // If there's remaining length, we need one more panel
  if (remaining > 0) {
    // Find smallest panel that covers the remaining
    for (let i = panelLengths.length - 1; i >= 0; i--) {
      if (panelLengths[i] >= remaining) {
        counts[String(panelLengths[i])]++;
        remaining = panelLengths[i] - remaining; // This becomes "extra"
        break;
      }
    }
  }

  const totalPanels =
    counts['8'] + counts['10'] + counts['12'] + counts['14'] + counts['16'] + counts['20'];

  return {
    length: targetLength,
    '8': counts['8'],
    '10': counts['10'],
    '12': counts['12'],
    '14': counts['14'],
    '16': counts['16'],
    '20': counts['20'],
    panels: totalPanels,
    extra: remaining,
    joints: Math.max(0, totalPanels - 1),
  };
}

/**
 * Original empirically-derived length table (1-100 feet).
 * These values have been validated against the legacy estimator.
 */
const originalLengthTable: LengthTableEntry[] = [
  { length: 1, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 7, joints: 0 },
  { length: 2, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 6, joints: 0 },
  { length: 3, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 5, joints: 0 },
  { length: 4, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 4, joints: 0 },
  { length: 5, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 3, joints: 0 },
  { length: 6, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 2, joints: 0 },
  { length: 7, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 1, joints: 0 },
  { length: 8, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 0, joints: 0 },
  { length: 9, '8': 0, '10': 1, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 1, joints: 0 },
  { length: 10, '8': 0, '10': 1, '12': 0, '14': 0, '16': 0, '20': 0, panels: 1, extra: 0, joints: 0 },
  { length: 11, '8': 0, '10': 0, '12': 1, '14': 0, '16': 0, '20': 0, panels: 1, extra: 1, joints: 0 },
  { length: 12, '8': 0, '10': 0, '12': 1, '14': 0, '16': 0, '20': 0, panels: 1, extra: 0, joints: 0 },
  { length: 13, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 0, panels: 1, extra: 1, joints: 0 },
  { length: 14, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 0, panels: 1, extra: 0, joints: 0 },
  { length: 15, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 0, panels: 1, extra: 1, joints: 0 },
  { length: 16, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 0, panels: 1, extra: 0, joints: 0 },
  { length: 17, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 1, panels: 1, extra: 3, joints: 0 },
  { length: 18, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 1, panels: 1, extra: 2, joints: 0 },
  { length: 19, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 1, panels: 1, extra: 1, joints: 0 },
  { length: 20, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 1, panels: 1, extra: 0, joints: 0 },
  { length: 21, '8': 0, '10': 1, '12': 1, '14': 0, '16': 0, '20': 0, panels: 2, extra: 1, joints: 1 },
  { length: 22, '8': 0, '10': 1, '12': 1, '14': 0, '16': 0, '20': 0, panels: 2, extra: 0, joints: 1 },
  { length: 23, '8': 0, '10': 0, '12': 2, '14': 0, '16': 0, '20': 0, panels: 2, extra: 1, joints: 1 },
  { length: 24, '8': 0, '10': 0, '12': 2, '14': 0, '16': 0, '20': 0, panels: 2, extra: 0, joints: 1 },
  { length: 25, '8': 0, '10': 0, '12': 1, '14': 1, '16': 0, '20': 0, panels: 2, extra: 1, joints: 1 },
  { length: 26, '8': 0, '10': 0, '12': 1, '14': 1, '16': 0, '20': 0, panels: 2, extra: 0, joints: 1 },
  { length: 27, '8': 0, '10': 0, '12': 0, '14': 2, '16': 0, '20': 0, panels: 2, extra: 1, joints: 1 },
  { length: 28, '8': 0, '10': 0, '12': 0, '14': 2, '16': 0, '20': 0, panels: 2, extra: 0, joints: 1 },
  { length: 29, '8': 0, '10': 0, '12': 0, '14': 1, '16': 1, '20': 0, panels: 2, extra: 1, joints: 1 },
  { length: 30, '8': 0, '10': 0, '12': 0, '14': 1, '16': 1, '20': 0, panels: 2, extra: 0, joints: 1 },
  { length: 31, '8': 0, '10': 0, '12': 0, '14': 0, '16': 2, '20': 0, panels: 2, extra: 1, joints: 1 },
  { length: 32, '8': 0, '10': 0, '12': 0, '14': 0, '16': 2, '20': 0, panels: 2, extra: 0, joints: 1 },
  { length: 33, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 1, panels: 2, extra: 1, joints: 1 },
  { length: 34, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 1, panels: 2, extra: 0, joints: 1 },
  { length: 35, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 1, panels: 2, extra: 1, joints: 1 },
  { length: 36, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 1, panels: 2, extra: 0, joints: 1 },
  { length: 37, '8': 0, '10': 1, '12': 0, '14': 2, '16': 0, '20': 0, panels: 3, extra: 1, joints: 2 },
  { length: 38, '8': 0, '10': 1, '12': 0, '14': 2, '16': 0, '20': 0, panels: 3, extra: 0, joints: 2 },
  { length: 39, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 2, panels: 2, extra: 1, joints: 1 },
  { length: 40, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 2, panels: 2, extra: 0, joints: 1 },
  { length: 41, '8': 0, '10': 1, '12': 0, '14': 0, '16': 2, '20': 0, panels: 3, extra: 1, joints: 2 },
  { length: 42, '8': 0, '10': 1, '12': 0, '14': 0, '16': 2, '20': 0, panels: 3, extra: 0, joints: 2 },
  { length: 43, '8': 0, '10': 0, '12': 1, '14': 0, '16': 2, '20': 0, panels: 3, extra: 1, joints: 2 },
  { length: 44, '8': 0, '10': 0, '12': 0, '14': 2, '16': 1, '20': 0, panels: 3, extra: 0, joints: 2 },
  { length: 45, '8': 0, '10': 0, '12': 0, '14': 1, '16': 2, '20': 0, panels: 3, extra: 1, joints: 2 },
  { length: 46, '8': 0, '10': 0, '12': 0, '14': 1, '16': 2, '20': 0, panels: 3, extra: 0, joints: 2 },
  { length: 47, '8': 0, '10': 0, '12': 0, '14': 0, '16': 3, '20': 0, panels: 3, extra: 1, joints: 2 },
  { length: 48, '8': 0, '10': 0, '12': 0, '14': 0, '16': 3, '20': 0, panels: 3, extra: 0, joints: 2 },
  { length: 49, '8': 0, '10': 1, '12': 0, '14': 0, '16': 0, '20': 2, panels: 3, extra: 1, joints: 2 },
  { length: 50, '8': 0, '10': 1, '12': 0, '14': 0, '16': 0, '20': 2, panels: 3, extra: 0, joints: 2 },
  { length: 51, '8': 0, '10': 0, '12': 1, '14': 0, '16': 0, '20': 2, panels: 3, extra: 1, joints: 2 },
  { length: 52, '8': 0, '10': 0, '12': 1, '14': 0, '16': 0, '20': 2, panels: 3, extra: 0, joints: 2 },
  { length: 53, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 2, panels: 3, extra: 1, joints: 2 },
  { length: 54, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 2, panels: 3, extra: 0, joints: 2 },
  { length: 55, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 2, panels: 3, extra: 1, joints: 2 },
  { length: 56, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 2, panels: 3, extra: 0, joints: 2 },
  { length: 57, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 3, panels: 3, extra: 3, joints: 2 },
  { length: 58, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 3, panels: 3, extra: 2, joints: 2 },
  { length: 59, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 3, panels: 3, extra: 1, joints: 2 },
  { length: 60, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 3, panels: 3, extra: 0, joints: 2 },
  { length: 61, '8': 0, '10': 0, '12': 0, '14': 1, '16': 3, '20': 0, panels: 4, extra: 1, joints: 3 },
  { length: 62, '8': 0, '10': 0, '12': 0, '14': 1, '16': 3, '20': 0, panels: 4, extra: 0, joints: 3 },
  { length: 63, '8': 0, '10': 0, '12': 0, '14': 0, '16': 4, '20': 0, panels: 4, extra: 1, joints: 3 },
  { length: 64, '8': 0, '10': 0, '12': 0, '14': 0, '16': 4, '20': 0, panels: 4, extra: 0, joints: 3 },
  { length: 65, '8': 0, '10': 0, '12': 1, '14': 1, '16': 0, '20': 2, panels: 4, extra: 1, joints: 3 },
  { length: 66, '8': 0, '10': 0, '12': 1, '14': 1, '16': 0, '20': 2, panels: 4, extra: 0, joints: 3 },
  { length: 67, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 3, panels: 4, extra: 1, joints: 3 },
  { length: 68, '8': 1, '10': 0, '12': 0, '14': 0, '16': 0, '20': 3, panels: 4, extra: 0, joints: 3 },
  { length: 69, '8': 0, '10': 1, '12': 0, '14': 0, '16': 0, '20': 3, panels: 4, extra: 1, joints: 3 },
  { length: 70, '8': 0, '10': 1, '12': 0, '14': 0, '16': 0, '20': 3, panels: 4, extra: 0, joints: 3 },
  { length: 71, '8': 0, '10': 0, '12': 1, '14': 0, '16': 0, '20': 3, panels: 4, extra: 1, joints: 3 },
  { length: 72, '8': 0, '10': 0, '12': 1, '14': 0, '16': 0, '20': 3, panels: 4, extra: 0, joints: 3 },
  { length: 73, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 3, panels: 4, extra: 1, joints: 3 },
  { length: 74, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 3, panels: 4, extra: 0, joints: 3 },
  { length: 75, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 3, panels: 4, extra: 1, joints: 3 },
  { length: 76, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 3, panels: 4, extra: 0, joints: 3 },
  { length: 77, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 4, panels: 4, extra: 3, joints: 3 },
  { length: 78, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 4, panels: 4, extra: 2, joints: 3 },
  { length: 79, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 4, panels: 4, extra: 1, joints: 3 },
  { length: 80, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 4, panels: 4, extra: 0, joints: 3 },
  { length: 81, '8': 1, '10': 0, '12': 0, '14': 1, '16': 0, '20': 3, panels: 5, extra: 1, joints: 4 },
  { length: 82, '8': 1, '10': 0, '12': 0, '14': 1, '16': 0, '20': 3, panels: 5, extra: 0, joints: 4 },
  { length: 83, '8': 0, '10': 1, '12': 0, '14': 1, '16': 0, '20': 3, panels: 5, extra: 1, joints: 4 },
  { length: 84, '8': 0, '10': 1, '12': 0, '14': 1, '16': 0, '20': 3, panels: 5, extra: 0, joints: 4 },
  { length: 85, '8': 0, '10': 0, '12': 1, '14': 1, '16': 0, '20': 3, panels: 5, extra: 1, joints: 4 },
  { length: 86, '8': 0, '10': 0, '12': 1, '14': 1, '16': 0, '20': 3, panels: 5, extra: 0, joints: 4 },
  { length: 87, '8': 0, '10': 0, '12': 0, '14': 2, '16': 0, '20': 3, panels: 5, extra: 1, joints: 4 },
  { length: 88, '8': 0, '10': 0, '12': 0, '14': 2, '16': 0, '20': 3, panels: 5, extra: 0, joints: 4 },
  { length: 89, '8': 0, '10': 0, '12': 0, '14': 1, '16': 1, '20': 3, panels: 5, extra: 1, joints: 4 },
  { length: 90, '8': 0, '10': 0, '12': 0, '14': 1, '16': 1, '20': 3, panels: 5, extra: 0, joints: 4 },
  { length: 91, '8': 0, '10': 0, '12': 0, '14': 0, '16': 2, '20': 3, panels: 5, extra: 1, joints: 4 },
  { length: 92, '8': 0, '10': 0, '12': 0, '14': 0, '16': 2, '20': 3, panels: 5, extra: 0, joints: 4 },
  { length: 93, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 4, panels: 5, extra: 1, joints: 4 },
  { length: 94, '8': 0, '10': 0, '12': 0, '14': 1, '16': 0, '20': 4, panels: 5, extra: 0, joints: 4 },
  { length: 95, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 4, panels: 5, extra: 1, joints: 4 },
  { length: 96, '8': 0, '10': 0, '12': 0, '14': 0, '16': 1, '20': 4, panels: 5, extra: 0, joints: 4 },
  { length: 97, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 5, panels: 5, extra: 3, joints: 4 },
  { length: 98, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 5, panels: 5, extra: 2, joints: 4 },
  { length: 99, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 5, panels: 5, extra: 1, joints: 4 },
  { length: 100, '8': 0, '10': 0, '12': 0, '14': 0, '16': 0, '20': 5, panels: 5, extra: 0, joints: 4 },
];

/**
 * Generate extended entries for lengths 101-200.
 * Uses calculated optimal combinations based on the same logic pattern.
 */
function generateExtendedEntries(): LengthTableEntry[] {
  const extended: LengthTableEntry[] = [];

  for (let len = 101; len <= 200; len++) {
    extended.push(calculateOptimalPanels(len));
  }

  return extended;
}

/**
 * Complete length table (1-200 feet)
 */
export const lengthTable: LengthTableEntry[] = [
  ...originalLengthTable,
  ...generateExtendedEntries(),
];

/**
 * Look up optimal panel combination for a given length.
 * Returns undefined if length is out of range.
 */
export function lookupLength(targetLength: number): LengthTableEntry | undefined {
  // Round to nearest integer
  const roundedLength = Math.round(targetLength);

  if (roundedLength < 1 || roundedLength > 200) {
    return undefined;
  }

  return lengthTable[roundedLength - 1]; // Array is 0-indexed
}

/**
 * Calculate optimal panels for any length (including > 200ft).
 * For lengths within table range, uses pre-computed values.
 * For lengths outside range, calculates on-the-fly.
 */
export function getOptimalPanels(targetLength: number): LengthTableEntry {
  const roundedLength = Math.round(targetLength);

  // Use lookup table if within range
  const tableEntry = lookupLength(roundedLength);
  if (tableEntry) {
    return tableEntry;
  }

  // Calculate for lengths outside table range
  return calculateOptimalPanels(roundedLength);
}

/**
 * Find the least-waste combination that uses exactly `panelCount` panels.
 * Ties on waste prefer fewer distinct SKUs, then longer panels.
 */
function findLeastWasteCombination(
  targetLength: number,
  panelCount: number
): LengthTableEntry | undefined {
  const panelLengths = [20, 16, 14, 12, 10, 8] as const;
  let best: { combo: number[]; waste: number; skus: number } | undefined;

  const search = (startIndex: number, combo: number[]) => {
    if (combo.length === panelCount) {
      const total = combo.reduce((sum, len) => sum + len, 0);
      if (total < targetLength) return;
      const waste = total - targetLength;
      const skus = new Set(combo).size;
      if (!best || waste < best.waste || (waste === best.waste && skus < best.skus)) {
        best = { combo: [...combo], waste, skus };
      }
      return;
    }
    // Non-increasing order enumerates each multiset once, longest panels first
    for (let i = startIndex; i < panelLengths.length; i++) {
      combo.push(panelLengths[i]);
      search(i, combo);
      combo.pop();
    }
  };
  search(0, []);

  if (!best) return undefined;

  const count = (len: number) => best!.combo.filter(l => l === len).length;
  return {
    length: targetLength,
    '8': count(8),
    '10': count(10),
    '12': count(12),
    '14': count(14),
    '16': count(16),
    '20': count(20),
    panels: panelCount,
    extra: best.waste,
    joints: panelCount - 1,
  };
}

/**
 * Optimal panel combination for a VERTICAL run (panels stacked up the wall).
 *
 * Rule: fewest panels (and therefore fewest horizontal H-trim seams), then
 * least waste. The length table already follows this rule for every row
 * except 37' and 38', which use 10+14+14 (3 panels, 2 seams) to save 2' of
 * waste over 20+20. On a vertical wall each extra seam is a full-wall-length
 * H-trim run installed at height, so the seam is avoided here. For every
 * other length this returns the table row unchanged, keeping vertical and
 * horizontal selection identical.
 */
export function getVerticalOptimalPanels(targetLength: number): LengthTableEntry {
  const roundedLength = Math.round(targetLength);
  const tableEntry = getOptimalPanels(roundedLength);
  const minPanels = Math.ceil(roundedLength / 20);

  const fewest = findLeastWasteCombination(roundedLength, minPanels);
  if (!fewest) return tableEntry;

  if (tableEntry.panels === fewest.panels && tableEntry.extra === fewest.extra) {
    return tableEntry;
  }
  return fewest;
}

/**
 * Get the number of H-trim joints needed for a given surface length.
 */
export function getJointsForLength(targetLength: number): number {
  const entry = getOptimalPanels(targetLength);
  return entry.joints;
}

/**
 * Get total panel count for a given length.
 */
export function getPanelCountForLength(targetLength: number): number {
  const entry = getOptimalPanels(targetLength);
  return entry.panels;
}

/**
 * Get waste (extra footage) for a given length.
 */
export function getWasteForLength(targetLength: number): number {
  const entry = getOptimalPanels(targetLength);
  return entry.extra;
}

export default lengthTable;
